import { CommonModule } from "@angular/common";
import {
  Component,
  HostListener,
  OnInit,
  OnDestroy,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import {
  ScheduledSmsService,
  AdminScheduledSms,
} from "../services/scheduled-sms.service";
import { SmsService } from "../services/sms.service";
@Component({
  selector: "sms-admin-scheduled",
  imports: [CommonModule, FormsModule],
  templateUrl: "./admin-scheduled-page.component.html",
  styleUrl: "./admin-scheduled-page.component.scss",
})
export class AdminScheduledPageComponent implements OnInit, OnDestroy {
  private readonly service = inject(ScheduledSmsService);
  private readonly sms = inject(SmsService);
  private generation = 0;
  private disposed = false;
  private timer?: ReturnType<typeof setInterval>;
  private debounce?: ReturnType<typeof setTimeout>;
  private opener: HTMLElement | null = null;
  items: AdminScheduledSms[] = [];
  users: { id: string; label: string }[] = [];
  total = 0;
  page = 1;
  pageSize = 25;
  readonly pageSizeOptions = [25, 50, 100];
  tab = "upcoming";
  userId = "";
  status = "";
  mode = "";
  search = "";
  loading = true;
  exporting = false;
  error = "";
  optionsError = "";
  selected: AdminScheduledSms | null = null;
  stats = {
    queued: 0,
    recipients: 0,
    estimated_cost: 0,
    next_at: null as string | null,
  };
  readonly tabs = [
    { key: "upcoming", label: "Próximos" },
    { key: "processed", label: "Procesados" },
    { key: "all", label: "Todos" },
  ];
  readonly statuses: Record<string, string> = {
    pending: "Programado",
    processing: "Enviando",
    sent: "Enviado",
    partial: "Parcial",
    failed: "Fallido",
    cancelled: "Cancelado",
  };
  readonly modes: Record<string, string> = {
    single: "Individual",
    multiple: "Múltiple",
    file: "Desde fichero",
    reminder: "Citas y recordatorios",
  };
  get totalPages() {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }
  get filters() {
    return {
      tab: this.tab,
      userId: this.userId,
      status: this.status,
      mode: this.mode,
      search: this.search,
      page: this.page,
      pageSize: this.pageSize,
    };
  }
  async ngOnInit() {
    await Promise.all([this.load(), this.loadUsers()]);
    if (!this.disposed)
      this.timer = setInterval(() => {
        if (!this.loading && !this.exporting) void this.load();
      }, 30000);
  }
  ngOnDestroy() {
    this.disposed = true;
    this.generation++;
    clearInterval(this.timer);
    clearTimeout(this.debounce);
    if (this.selected) document.body.style.overflow = "";
  }
  async loadUsers() {
    try {
      const options = await this.sms.adminMessageOptions();
      if (!this.disposed) {
        this.users = options.users;
        this.optionsError = "";
      }
    } catch {
      if (!this.disposed)
        this.optionsError = "No se pudieron cargar los clientes.";
    }
  }
  async load() {
    const id = ++this.generation;
    this.loading = true;
    this.error = "";
    try {
      const result = await this.service.adminList(this.filters);
      if (this.disposed || id !== this.generation) return;
      this.items = result.items;
      this.total = Number(result.total);
      this.stats = result.stats;
      if (this.selected) {
        const updated = result.items.find((i) => i.id === this.selected?.id);
        if (updated) this.selected = updated;
      }
      if (this.page > this.totalPages) {
        this.page = this.totalPages;
        await this.load();
      }
    } catch (e) {
      if (id === this.generation && !this.disposed)
        this.error =
          e instanceof Error
            ? e.message
            : "No se pudieron cargar los programados.";
    } finally {
      if (id === this.generation && !this.disposed) this.loading = false;
    }
  }
  filter() {
    this.page = 1;
    void this.load();
  }
  changePageSize(value: number) {
    this.pageSize = value;
    this.filter();
  }
  searchChanged() {
    this.generation++;
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.filter(), 400);
  }
  clear() {
    this.userId = "";
    this.status = "";
    this.mode = "";
    this.search = "";
    this.filter();
  }
  date(value: string | null | undefined) {
    return value
      ? new Date(value).toLocaleString("es-PE", {
          timeZone: "America/Lima",
          dateStyle: "medium",
          timeStyle: "short",
        })
      : "No disponible";
  }
  overdue(item: AdminScheduledSms) {
    return (
      item.status === "pending" && Date.parse(item.scheduled_at) < Date.now()
    );
  }
  open(item: AdminScheduledSms) {
    this.opener = document.activeElement as HTMLElement;
    this.selected = item;
    document.body.style.overflow = "hidden";
    setTimeout(() => document.getElementById("scheduled-close")?.focus());
  }
  @HostListener("document:keydown.escape") close() {
    this.selected = null;
    document.body.style.overflow = "";
    this.opener?.focus();
  }
  @HostListener("document:keydown", ["$event"]) trap(e: KeyboardEvent) {
    if (this.selected && e.key === "Tab") {
      const nodes = Array.from(
        document.querySelectorAll<HTMLElement>(".schedule-modal button"),
      );
      const first = nodes[0],
        last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    }
  }
  async exportCsv() {
    if (this.exporting) return;
    this.exporting = true;
    this.error = "";
    const filters = { ...this.filters };
    try {
      const rows: AdminScheduledSms[] = [];
      for (let page = 1; ; page++) {
        const result = await this.service.adminList({ ...filters, page });
        if (this.disposed) return;
        rows.push(...result.items);
        if (rows.length >= result.total || !result.items.length) break;
      }
      const cell = (v: unknown) => {
        let s = String(v ?? "");
        if (/^[=+@\-\t\r]/.test(s)) s = "'" + s;
        return '"' + s.replace(/"/g, '""') + '"';
      };
      const csv = [
        [
          "Programado",
          "Cliente",
          "Email",
          "Nombre",
          "Modalidad",
          "Destinatarios",
          "Estado",
          "Enviados",
          "Fallidos",
          "Costo registrado",
          "Creado",
        ],
        ...rows.map((i) => [
          this.date(i.scheduled_at),
          i.client?.full_name,
          i.client?.email,
          i.name,
          this.modes[i.mode],
          i.total_recipients,
          this.statuses[i.status],
          i.sent_count,
          i.failed_count,
          i.actual_cost,
          this.date(i.created_at),
        ]),
      ]
        .map((row) => row.map(cell).join(","))
        .join("\r\n");
      const url = URL.createObjectURL(
        new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "programados.csv";
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      this.error = e instanceof Error ? e.message : "No se pudo exportar.";
    } finally {
      this.exporting = false;
    }
  }
}
